// SEND THE REPORT TO A RECIPIENT LIST (owner, 2026-10-21: "when i select a
// time period, or all list of filtered items get checked and generate a
// report out of them, i get a preview window with the list of recipients ...
// for all contractors").
//
// The window shows who receives which copy before anything is sent:
//   Project team    the whole report, to the owning company's people;
//   each contractor only their own lines and totals, to their people on the
//                   project roster.
// Beside each person: whether their copy carries cost. That is the server's
// rule for every emailed report (back/utils/reportCostReaders), shown here,
// never decided here. The mail itself is previewed as the team receives it.
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Checkbox, Collapse, Empty, Input, InputNumber, Modal, Select, Space, Spin, Tag, Tooltip, Typography, message } from "antd";
import { previewReportSend, sendReport } from "api/billableHours";

const { Text } = Typography;
const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const hrs = (n) => `${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })} h`;

function PeopleList({ people, picked, onToggle }) {
  if (!people.length) return <Text type="secondary">Nobody on the roster.</Text>;
  return (
    <div>
      {people.map((p) => (
        <div key={p.email} style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 0" }}>
          <Checkbox checked={picked.has(p.email)} onChange={(e) => onToggle(p.email, e.target.checked)}>
            <b>{p.name}</b> <Text type="secondary">{p.email}</Text>
          </Checkbox>
          {p.jobTitle && <Tag>{p.jobTitle}</Tag>}
          {p.kind === "supervisor" && <Tag color="blue">Supervisor</Tag>}
          {p.kind === "executive" && <Tag color="purple">Executive</Tag>}
          {p.you && <Tag color="green">You</Tag>}
          {!p.hasAccount && <Tag>No account</Tag>}
          {p.cost
            ? <Tag color="gold">With cost</Tag>
            : <Tooltip title={p.costReason}><Tag>Hours only</Tag></Tooltip>}
        </div>
      ))}
    </div>
  );
}

/**
 * THE SAME PEOPLE, PICKED BY TYPING (owner, 2026-10-26: "dropdown with search and
 * selection like the assignee dropdown of issues").
 *
 * It sits ABOVE the list rather than replacing it, because the list carries what the
 * dropdown cannot: who gets cost and who gets hours only, and why. Searching a long
 * roster is what the dropdown is for; reading what each person will receive is what
 * the list is for. Both drive the same set, so neither can disagree with the other.
 */
function PeopleSearch({ people, picked, onSet }) {
  if (people.length < 2) return null;
  return (
    <Select
      mode="multiple"
      showSearch
      allowClear
      optionFilterProp="label"
      style={{ width: "100%", marginBottom: 6 }}
      placeholder="Search people by name or address"
      value={[...picked]}
      onChange={(v) => onSet(new Set(v))}
      maxTagCount="responsive"
      options={people.map((p) => ({
        value: p.email,
        // searched on, so typing either a name or an address finds the person
        label: `${p.name || ""} ${p.email}${p.jobTitle ? ` ${p.jobTitle}` : ""}`.trim(),
        title: p.cost ? "Will receive cost" : p.costReason || "Hours only",
      }))}
    />
  );
}

const startPicked = (people) => new Set(people.filter((p) => p.checked).map((p) => p.email));

export default function SendReportModal({ open, onClose, query, items, prices }) {
  const [state, setState] = useState({ loading: false, data: null, error: "" });
  const [team, setTeam] = useState(new Set());
  const [byCo, setByCo] = useState({});
  const [note, setNote] = useState("");
  // TYPED ADDRESSES (owner: "send it to specific emails") and ONE RATE FOR THIS
  // DOCUMENT ONLY. Neither is stored anywhere - they live for this send.
  const [emails, setEmails] = useState([]);
  const [flatRate, setFlatRate] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setState({ loading: true, data: null, error: "" });
    setResult(null);
    previewReportSend({ query, items, prices })
      .then((data) => {
        if (!live) return;
        setState({ loading: false, data, error: "" });
        setTeam(startPicked(data.team || []));
        setByCo(Object.fromEntries((data.contractors || []).map((c) => [c.companyId, startPicked(c.people)])));
      })
      .catch((e) => { if (live) setState({ loading: false, data: null, error: (e && (e.err || e.message)) || "Could not prepare the report" }); });
    return () => { live = false; };
  }, [open, query, items, prices]);

  const d = state.data;
  const count = team.size + Object.values(byCo).reduce((a, s) => a + s.size, 0) + emails.length;
  const toggle = (set, email, on) => { const n = new Set(set); if (on) n.add(email); else n.delete(email); return n; };
  const all = (people, on) => new Set(on ? people.map((p) => p.email) : []);

  const send = () => {
    setSending(true);
    sendReport({
      query, items, prices, message: note,
      emails,
      ...(flatRate == null || flatRate === "" ? {} : { flatRate }),
      recipients: { team: [...team], contractors: Object.fromEntries(Object.entries(byCo).map(([k, s]) => [k, [...s]])) },
    })
      .then((r) => {
        setResult(r);
        if (r.failed && r.failed.length) message.warning(`Sent to ${r.sent.length}; ${r.failed.length} could not be sent`);
        else message.success(`Report sent to ${r.sent.length} ${r.sent.length === 1 ? "person" : "people"}`);
      })
      .catch((e) => message.error((e && (e.err || e.message)) || "Could not send the report"))
      .finally(() => setSending(false));
  };

  const previewDoc = useMemo(() => (d ? `<!doctype html><html><body style="margin:12px">${d.html}</body></html>` : ""), [d]);

  return (
    <Modal
      open={open} onCancel={onClose} width={860} destroyOnClose
      title={d ? `Send the report - ${d.project.title}` : "Send the report"}
      footer={result
        ? [<Button key="done" type="primary" onClick={onClose}>Done</Button>]
        : [
          <Button key="cancel" onClick={onClose}>Cancel</Button>,
          <Button key="send" type="primary" disabled={!d || !count} loading={sending} onClick={send}>
            Send to {count} {count === 1 ? "person" : "people"}
          </Button>,
        ]}
    >
      {state.loading && <div style={{ textAlign: "center", padding: 32 }}><Spin /></div>}
      {state.error && <Alert type="error" showIcon message={state.error} />}
      {result && (
        <Alert
          type={result.failed.length ? "warning" : "success"} showIcon
          message={`Sent to ${result.sent.length} ${result.sent.length === 1 ? "person" : "people"}${result.failed.length ? `; ${result.failed.length} could not be sent` : ""}`}
          description={result.failed.length ? result.failed.map((f) => `${f.email}: ${f.err}`).join("; ") : "Each delivery shows in Email delivery."}
        />
      )}
      {d && !result && (
        <>
          <Space wrap style={{ marginBottom: 12 }}>
            <Tag>{d.period}</Tag>
            <Tag>{d.lines} line{d.lines === 1 ? "" : "s"}</Tag>
            {d.selected && <Tag color="blue">Ticked lines only</Tag>}
            <Tag>{hrs(d.totals.hours)}</Tag>
            {"cost" in d.totals && <Tag color="gold">{money(d.totals.cost)}</Tag>}
            {d.facts.levels.length > 0 && <Tag>Levels: {d.facts.levels.join(", ")}</Tag>}
          </Space>
          {(d.notes || []).map((n) => <Alert key={n} type="info" showIcon message={n} style={{ marginBottom: 8 }} />)}

          <Card size="small" style={{ marginBottom: 10 }}
            title="Project team - the whole report"
            extra={<Space><Button size="small" type="link" onClick={() => setTeam(all(d.team, true))}>All</Button><Button size="small" type="link" onClick={() => setTeam(all(d.team, false))}>None</Button></Space>}>
            <PeopleSearch people={d.team} picked={team} onSet={setTeam} />
            <PeopleList people={d.team} picked={team} onToggle={(e, on) => setTeam((s) => toggle(s, e, on))} />
          </Card>

          {!d.contractors.length && <Empty description="No contractor has lines in this report." />}
          {d.contractors.map((c) => (
            <Card key={c.companyId} size="small" style={{ marginBottom: 10 }}
              title={<span>{c.label} <Text type="secondary" style={{ fontWeight: 400 }}>- their lines only: {c.lines} line{c.lines === 1 ? "" : "s"}, {hrs(c.hours)}{"cost" in c ? `, ${money(c.cost)}` : ""}</Text></span>}
              extra={c.people.length > 0 && <Space><Button size="small" type="link" onClick={() => setByCo((m) => ({ ...m, [c.companyId]: all(c.people, true) }))}>All</Button><Button size="small" type="link" onClick={() => setByCo((m) => ({ ...m, [c.companyId]: all(c.people, false) }))}>None</Button></Space>}>
              {c.note && <Alert type="warning" showIcon message={c.note} style={{ marginBottom: 6 }} />}
              <PeopleSearch people={c.people} picked={byCo[c.companyId] || new Set()} onSet={(v) => setByCo((m) => ({ ...m, [c.companyId]: v }))} />
              <PeopleList people={c.people} picked={byCo[c.companyId] || new Set()} onToggle={(e, on) => setByCo((m) => ({ ...m, [c.companyId]: toggle(m[c.companyId] || new Set(), e, on) }))} />
            </Card>
          ))}

          {/* ADDRESSES NOT ON ANY ROSTER. They receive the PROJECT TEAM copy - the
              whole report shown here - because there is no contractor to scope a
              stranger to, and sending one contractor's lines to an address we
              cannot place would be the worst possible default. */}
          <Card size="small" style={{ marginBottom: 10 }} title="Send to other addresses">
            <Select
              mode="tags"
              id="send-report-extra-emails"
              style={{ width: "100%" }}
              value={emails}
              onChange={setEmails}
              tokenSeparators={[",", " ", ";"]}
              placeholder="Type an email address and press enter"
              open={false}
              suffixIcon={null}
            />
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
              They get the project team copy - the whole report above, not one contractor&apos;s lines.
              {prices !== false ? " It carries cost." : " It carries hours only."}
            </Text>
          </Card>

          {/* ONE RATE FOR THIS DOCUMENT ONLY. Nothing is saved: the rates on file,
              the page and every later report are untouched. */}
          {prices !== false && (
            <Card size="small" style={{ marginBottom: 10 }} title="Price this report at one rate">
              <Space align="start">
                <InputNumber
                  min={0}
                  max={10000}
                  step={5}
                  value={flatRate}
                  onChange={setFlatRate}
                  addonBefore="$"
                  addonAfter="/h"
                  placeholder="leave empty"
                  style={{ width: 190 }}
                />
                {flatRate != null && flatRate !== "" && (
                  <Button size="small" type="link" onClick={() => setFlatRate(null)}>Use the rates on file</Button>
                )}
              </Space>
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
                Every line in this send is priced at this rate. Nothing is saved - the rates on file and
                the page do not change.
              </Text>
              {flatRate != null && flatRate !== "" && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 6 }}
                  message={`This document will say it was priced at $${flatRate}/h for every line, in place of the rates on file.`}
                  description="No copy of it is kept, so there will be nothing on our side to check against if a figure is later disputed."
                />
              )}
            </Card>
          )}

          <Input.TextArea
            value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} autoSize={{ minRows: 2, maxRows: 5 }}
            placeholder="A note at the top of the email (optional)" style={{ margin: "6px 0 10px" }}
          />
          <Collapse size="small" items={[{
            key: "mail",
            label: <span>Email preview (project team copy): <Text type="secondary">{d.subject}</Text></span>,
            children: (
              <>
                <Text type="secondary" style={{ display: "block", marginBottom: 6 }}>
                  Each contractor&apos;s copy names them and carries only their lines and totals. The PDF report is attached to every copy.
                </Text>
                <iframe title="Email preview" sandbox="" srcDoc={previewDoc} style={{ width: "100%", height: 420, border: "1px solid #eee", borderRadius: 6 }} />
              </>
            ),
          }]} />
        </>
      )}
    </Modal>
  );
}
